import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../services/api';
import { CANALES, eur, fechaCuota } from './paymentPlanUtils';

/**
 * Los planes de pago vigentes que ve el usuario (`GET /payment-plans/agreements`):
 * el admin, todos; la empresa, los suyos; su equipo, los de sus expedientes.
 * El servidor decide el ámbito; esto solo lo pinta.
 *
 * Los que tienen cuotas vencidas van primero, y a igual retraso, el que más debe.
 */
const canal = v => CANALES.find(c => c.value === v)?.label || v;

const maxVencida = p => Math.max(0, ...p.installments.map(c => c.overdue_days || 0));

export const ordenar = planes =>
  [...planes].sort((a, b) => maxVencida(b) - maxVencida(a) || (b.current_debt || 0) - (a.current_debt || 0));

const PaymentPlansList = () => {
  const [estado, setEstado] = useState({ status: 'loading', data: [], error: null });
  const [soloVencidos, setSoloVencidos] = useState(false);

  useEffect(() => {
    let vivo = true;
    api
      .get('/payment-plans/agreements')
      .then(({ data }) => vivo && setEstado({ status: 'ok', data: data.data || [], error: null }))
      .catch(err => vivo && setEstado({ status: 'error', data: [], error: getErrorMessage(err) }));
    return () => {
      vivo = false;
    };
  }, []);

  if (estado.status === 'loading') return <p className='px-4 py-6 text-sm text-slate-400'>Cargando…</p>;
  if (estado.status === 'error') return <p className='px-4 py-6 text-sm text-rose-700'>{estado.error}</p>;

  const filas = ordenar(estado.data).filter(p => !soloVencidos || maxVencida(p) > 0);

  return (
    <div className='bg-white border border-slate-200 rounded-lg'>
      <div className='flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200'>
        <span className='text-sm text-slate-600'>
          {estado.data.length} {estado.data.length === 1 ? 'plan vigente' : 'planes vigentes'}
        </span>
        <label className='inline-flex items-center gap-2 text-sm text-slate-600'>
          <input type='checkbox' className='form-checkbox rounded-lg' checked={soloVencidos} onChange={e => setSoloVencidos(e.target.checked)} />
          Solo con cuotas vencidas
        </label>
      </div>

      {filas.length === 0 ? (
        // Vacío no es «todo al día»: es que con este filtro no sale ninguno.
        <p className='px-4 py-6 text-sm text-slate-500'>Ninguno con estos filtros.</p>
      ) : (
        <div className='overflow-x-auto'>
          <table className='min-w-full text-sm text-slate-700'>
            <thead className='text-xs text-slate-500 bg-slate-50'>
              <tr>
                <th className='px-3 py-2 text-left font-medium'>Expediente</th>
                <th className='px-3 py-2 text-left font-medium'>Próxima cuota</th>
                <th className='px-3 py-2 text-left font-medium'>Vencidas</th>
                <th className='px-3 py-2 text-right font-medium'>Pagado</th>
                <th className='px-3 py-2 text-right font-medium'>Deuda viva</th>
                <th className='px-3 py-2 text-left font-medium'>Seguimiento</th>
              </tr>
            </thead>
            <tbody>
              {filas.map(p => {
                const vencidas = p.installments.filter(c => c.overdue_days > 0);
                return (
                  <tr key={p._id} className='border-t border-slate-100 align-top'>
                    <td className='px-3 py-2'>
                      {/* El deudor y el cliente: `nombre_comercial` es la marca («Axi Credit»), no a quién se cobra. */}
                      <Link to={`/view/${p.expedient_id}`} className='text-indigo-600 hover:underline font-medium'>
                        {p.debtor_name || 'Deudor sin nombre'}
                      </Link>
                      <div className='text-xs text-slate-500'>
                        {[p.client_name || p.expedient?.nombre_comercial, `${p.installments.length} cuotas · ${eur(p.total_amount)}`]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>
                    <td className='px-3 py-2 whitespace-nowrap'>
                      {p.next_installment ? (
                        <>
                          {fechaCuota(p.next_installment.due_date)}
                          <div className='text-xs text-slate-500'>
                            Cuota {p.next_installment.number} · {eur(p.next_installment.amount)}
                          </div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className='px-3 py-2'>
                      {vencidas.length ? (
                        <span className='text-rose-700'>
                          {vencidas.length} · la más antigua hace {maxVencida(p)} d
                        </span>
                      ) : (
                        <span className='text-emerald-700'>Al día</span>
                      )}
                    </td>
                    <td className='px-3 py-2 text-right whitespace-nowrap'>{eur(p.paid_total)}</td>
                    <td className='px-3 py-2 text-right whitespace-nowrap'>
                      {eur(p.current_debt)}
                      {p.debt_mismatch && (
                        <div className='inline-flex items-center gap-1 text-xs text-amber-700'>
                          <ExclamationTriangleIcon className='w-4 h-4' /> más que el plan
                        </div>
                      )}
                    </td>
                    <td className='px-3 py-2'>{canal(p.channel)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PaymentPlansList;
